import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  Policy as PolicyIcon,
  Assignment as ContractIcon,
  Home as LeaseIcon,
  Badge as LicenseIcon,
  Description as DocumentIcon,
  Article as EndorsementIcon,
  Check as CheckIcon,
  ArrowBack,
  ArrowForward,
  Send as SendIcon,
  InsertDriveFile as CertificateIcon,
  Add as AddIcon
} from '@mui/icons-material';
import CertificatePicker from './CertificatePicker';
import { awsDateToUS } from 'utils/dateUtils';

const DOCUMENT_CATEGORIES = [
  {
    value: 'POLICY',
    label: 'Policy',
    icon: <PolicyIcon />,
    description: 'Insurance policies and coverage documents',
    placeholder: 'Please provide the general liability policy for the current term'
  },
  {
    value: 'ENDORSEMENT',
    label: 'Endorsement',
    icon: <EndorsementIcon />,
    description: 'Policy endorsements and amendments',
    placeholder: 'Please provide any additional insured endorsements'
  },
  {
    value: 'CONTRACT',
    label: 'Contract',
    icon: <ContractIcon />,
    description: 'Service agreements and contracts',
    placeholder: 'Please provide the signed service agreement'
  },
  {
    value: 'LEASE',
    label: 'Lease',
    icon: <LeaseIcon />,
    description: 'Lease agreements and rental documents',
    placeholder: 'Please provide the current lease agreement for your business location'
  },
  {
    value: 'DRIVERS_LICENSE',
    label: "Driver's License",
    icon: <LicenseIcon />,
    description: 'Driver licenses for operators',
    placeholder: 'Please provide valid driver licenses for all vehicle operators'
  },
  {
    value: 'OTHER',
    label: 'Other',
    icon: <DocumentIcon />,
    description: 'Any other documents',
    placeholder: 'Please describe the specific documents you need'
  }
];

const DocumentRequestForm = ({ open, onClose, vendor, onSubmit, certificates = [], isTourMode = false, tourWizardStep }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [categoryDescriptions, setCategoryDescriptions] = useState({});
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [showCertificatePicker, setShowCertificatePicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsCertificateSelection, setNeedsCertificateSelection] = useState(false);

  // Tour mode: pre-select categories and descriptions for demo
  useEffect(() => {
    if (isTourMode && open) {
      // Pre-select CONTRACT category (doesn't require certificate selection step)
      setSelectedCategories({ CONTRACT: true });
      setCategoryDescriptions({ CONTRACT: 'Please provide the signed service agreement for the current project.' });
      setNeedsCertificateSelection(false);
    }
  }, [isTourMode, open]);

  // Tour mode: navigate to the correct wizard step
  useEffect(() => {
    if (isTourMode && tourWizardStep !== undefined && tourWizardStep !== activeStep) {
      setActiveStep(tourWizardStep);
    }
  }, [isTourMode, tourWizardStep, activeStep]);

  // Check if certificate selection is needed based on selected categories
  useEffect(() => {
    if (isTourMode) return; // Skip in tour mode - we control this manually
    const needsCerts = Object.entries(selectedCategories)
      .filter(([_, selected]) => selected)
      .some(([category]) =>
        category === 'POLICY' ||
        category === 'ENDORSEMENT' ||
        category === 'OTHER'
      );
    setNeedsCertificateSelection(needsCerts);
  }, [selectedCategories, isTourMode]);

  const steps = needsCertificateSelection 
    ? ['Select Documents', 'Select Certificates', 'Add Details', 'Review & Send']
    : ['Select Documents', 'Add Details', 'Review & Send'];

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
    
    // Clear description if unchecked
    if (selectedCategories[category]) {
      setCategoryDescriptions(prev => {
        const newDesc = { ...prev };
        delete newDesc[category];
        return newDesc;
      });
    }
  };

  const handleDescriptionChange = (category, value) => {
    setCategoryDescriptions(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate at least one category selected
      const hasSelection = Object.values(selectedCategories).some(v => v);
      if (!hasSelection) {
        setError('Please select at least one document type');
        return;
      }
    }
    
    // Certificate selection step (only if needed)
    if (needsCertificateSelection && activeStep === 1) {
      // Certificates are optional, so no validation needed
    }
    
    // Description step
    const descriptionStepIndex = needsCertificateSelection ? 2 : 1;
    if (activeStep === descriptionStepIndex) {
      // Validate all selected categories have descriptions
      const selectedCats = Object.keys(selectedCategories).filter(k => selectedCategories[k]);
      const missingDesc = selectedCats.some(cat => !categoryDescriptions[cat]?.trim());
      if (missingDesc) {
        setError('Please provide descriptions for all selected document types');
        return;
      }
    }

    setError(null);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const selectedCats = Object.keys(selectedCategories)
        .filter(k => selectedCategories[k])
        .map(category => ({
          category,
          description: categoryDescriptions[category],
          required: true
        }));

      await onSubmit({
        requestType: 'OTHER',
        categories: selectedCats,
        vendor,
        associatedCertificateIds: selectedCertificates,
        certificates: certificates.filter(cert => selectedCertificates.includes(cert.id))
      });

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to send document request');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedCount = () => {
    return Object.values(selectedCategories).filter(v => v).length;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'document-request-modal',
        sx: {
          borderRadius: 2,
          minHeight: '600px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box>
          <Typography variant="h5" fontWeight="600">
            Request Documents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {vendor?.name || vendor?.entityName || 'Vendor'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent className="document-request-content" sx={{ pt: 3 }}>
        <Stepper activeStep={activeStep} className="document-request-stepper" sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Select Categories */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Document Types
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose the types of documents you need from this vendor
            </Typography>
            
            <FormGroup className="document-type-selection">
              {DOCUMENT_CATEGORIES.map((category) => (
                <Card key={category.value} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedCategories[category.value] || false}
                          onChange={() => handleCategoryToggle(category.value)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ color: 'primary.main' }}>
                            {category.icon}
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="500">
                              {category.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {category.description}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </CardContent>
                </Card>
              ))}
            </FormGroup>
          </Box>
        )}

        {/* Step 2: Select Certificates (conditional) */}
        {needsCertificateSelection && activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Associated Certificates (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select certificates that these documents relate to. The vendor will be able to view these certificates when uploading documents.
            </Typography>
            
            {selectedCertificates.length === 0 ? (
              <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <CertificateIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  No certificates selected
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select certificates to provide context for the requested documents
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCertificatePicker(true)}
                >
                  Select Certificates
                </Button>
              </Card>
            ) : (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    SELECTED CERTIFICATES ({selectedCertificates.length})
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setShowCertificatePicker(true)}
                  >
                    Change Selection
                  </Button>
                </Stack>
                
                <List>
                  {certificates
                    .filter(cert => selectedCertificates.includes(cert.id))
                    .map(cert => (
                      <Card key={cert.id} variant="outlined" sx={{ mb: 1 }}>
                        <ListItem>
                          <ListItemIcon>
                            <CertificateIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body1">
                                  {cert.insuredName || cert.insured || 'Certificate'}
                                </Typography>
                                <Chip 
                                  label={cert.status} 
                                  size="small" 
                                  color={cert.status === 'APPROVED' ? 'success' : 'default'}
                                  sx={cert.status === 'REVIEW' ? {
                                    backgroundColor: 'rgba(128, 0, 128, 0.1)',
                                    color: 'purple',
                                    '&:hover': {
                                      backgroundColor: 'rgba(128, 0, 128, 0.2)'
                                    }
                                  } : {}}
                                />
                              </Stack>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" display="block">
                                  Holder: {cert.certificateHolder || '-'}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  Expires: {cert.firstExp && cert.firstExp !== '0000-01-01' ? awsDateToUS(cert.firstExp) : '-'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      </Card>
                    ))
                  }
                </List>
              </Box>
            )}
            
            <Alert severity="info" sx={{ mt: 3 }}>
              Certificate selection is optional. If selected, the vendor will see these certificates in read-only mode when uploading documents.
            </Alert>
          </Box>
        )}

        {/* Step 3 (or 2): Add Descriptions */}
        {activeStep === (needsCertificateSelection ? 2 : 1) && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Provide Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add specific instructions for each document type
            </Typography>
            
            <Stack spacing={3}>
              {Object.keys(selectedCategories)
                .filter(k => selectedCategories[k])
                .map(categoryKey => {
                  const category = DOCUMENT_CATEGORIES.find(c => c.value === categoryKey);
                  return (
                    <Card key={categoryKey} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Box sx={{ color: 'primary.main' }}>
                            {category.icon}
                          </Box>
                          <Typography variant="subtitle1" fontWeight="500">
                            {category.label}
                          </Typography>
                        </Box>
                        
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Description / Instructions"
                          placeholder={category.placeholder}
                          value={categoryDescriptions[categoryKey] || ''}
                          onChange={(e) => handleDescriptionChange(categoryKey, e.target.value)}
                          helperText="Provide specific details about what you need"
                        />
                      </CardContent>
                    </Card>
                  );
                })}
            </Stack>
          </Box>
        )}

        {/* Step 4 (or 3): Review */}
        {activeStep === (needsCertificateSelection ? 3 : 2) && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Request
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please review the documents you're requesting before sending
            </Typography>
            
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  VENDOR
                </Typography>
                <Typography variant="body1" fontWeight="500" gutterBottom>
                  {vendor?.name || vendor?.entityName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {vendor?.email}
                </Typography>
              </CardContent>
            </Card>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              REQUESTED DOCUMENTS ({getSelectedCount()})
            </Typography>
            
            <Stack spacing={2}>
              {Object.keys(selectedCategories)
                .filter(k => selectedCategories[k])
                .map(categoryKey => {
                  const category = DOCUMENT_CATEGORIES.find(c => c.value === categoryKey);
                  return (
                    <Card key={categoryKey} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                            {category.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight="500">
                              {category.label}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {categoryDescriptions[categoryKey]}
                            </Typography>
                          </Box>
                          <CheckIcon color="success" />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
            </Stack>

            {selectedCertificates.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  ASSOCIATED CERTIFICATES ({selectedCertificates.length})
                </Typography>
                <Stack spacing={1}>
                  {certificates
                    .filter(cert => selectedCertificates.includes(cert.id))
                    .map(cert => (
                      <Card key={cert.id} variant="outlined">
                        <CardContent sx={{ py: 1 }}>
                          <Typography variant="body2">
                            {cert.insuredName || cert.insured} - {cert.certificateHolder}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))
                  }
                </Stack>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 3 }}>
              The vendor will receive an email with a secure link to upload the requested documents. 
              The link will expire in 7 days.
            </Alert>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions className="document-request-actions" sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        px: 3,
        py: 2
      }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 && (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForward />}
            disabled={activeStep === 0 && getSelectedCount() === 0}
          >
            Next
          </Button>
        )}
        
        {activeStep === steps.length - 1 && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? null : <SendIcon />}
          >
            {loading ? 'Sending...' : 'Send Request'}
          </Button>
        )}
      </DialogActions>
      
      {/* Certificate Picker Modal */}
      <CertificatePicker
        open={showCertificatePicker}
        onClose={() => setShowCertificatePicker(false)}
        onSelect={(selected) => {
          setSelectedCertificates(selected);
          setShowCertificatePicker(false);
        }}
        certificates={certificates}
        selectedCertificateIds={selectedCertificates}
        vendorName={vendor?.name || vendor?.entityName}
      />
    </Dialog>
  );
};

export default DocumentRequestForm;